import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@material-ui/core';

import * as actions from 'memberPortalModules/MemberProfile/state/invoice/actions';

import { CartUnitTypes, IInvoiceDetailsImt } from 'common/interfaces/invoices';

import messages from 'common/messages/messages';

import { CustomTheme } from 'common/ui/interfaces';
import { useSearchInvoiceProductsState } from 'common/createContext/searchInvoiceProductsContext';
import { useAppDispatch } from 'store/hooks';
import InvoiceTopUpBalanceItem from 'common/components/InvoiceOperating/InvoiceTopUpBalanceItem/InvoiceTopUpBalanceItem';
import InvoiceProductItem from 'common/components/InvoiceOperating/InvoiceProductItem/InvoiceProductItem';
import InvoiceServiceItem from 'common/components/InvoiceOperating/InvoiceServiceItem/InvoiceServiceItem';
import InvoiceBillingItem from 'common/components/InvoiceOperating/InvoiceBillingItem/InvoiceBillingItem';
import InvoiceGiftCardItem from 'common/components/InvoiceOperating/InvoiceGiftCardItem/InvoiceGiftCardItem';
import InvoicePastDueItem from 'common/components/InvoiceOperating/InvoicePastDueItem/InvoicePastDueItem';
import InvoiceCancelPackageItem from 'common/components/InvoiceOperating/InvoiceCancelPackageItem/InvoiceCancelPackageItem';
import { transformInvoiceBundleDTO } from 'common/components/InvoiceOperating/utils';

const useStyles = makeStyles((theme: CustomTheme) => ({
  addButton: {
    padding: theme.spacing(1, 1.5),

    '& svg': {
      width: '1rem',
      height: '1rem',
    },
  },
  shiftedButton: {
    marginLeft: theme.spacing(-1),
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
  },
  actionItemIcon: {
    width: '1rem',
    height: '1rem',
    marginRight: theme.spacing(1),
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 2),
    minHeight: 0,
  },
  emptyCartText: {
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
  },
  cartItem: {
    borderBottom: `1px solid ${theme.palette.borderColor?.light}`,
  },
  bordered: {
    padding: theme.spacing(1, 0),
    margin: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.borderColor?.light}`,
  },
}));

interface IInvoiceCreationCartProps {
  memberInvoice?: IInvoiceDetailsImt;
}

export default function InvoiceCreationCart({
  memberInvoice,
}: IInvoiceCreationCartProps): JSX.Element {
  const dispatch = useAppDispatch();

  const {
    showSearchProducts: { isShowAddInventories, isShowAddServicePackages, isShowAddGiftCards },
  } = useSearchInvoiceProductsState();

  const classes = useStyles();

  const transformedInvoice = useMemo(() => memberInvoice?.toJS(), [memberInvoice]);

  const disabled = (transformedInvoice?.invoicePaymentSplits || []).length > 0;

  const isSearchProductsActive =
    isShowAddInventories || isShowAddServicePackages || isShowAddGiftCards;

  const isCartEmpty = !transformedInvoice?.invoiceUnits?.length;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onProductCountChange = (invoiceUnitId: string, count: number) => {};

  const onInvoiceUnitDelete = (invoiceUnitId: string) => {
    dispatch(actions.deleteInvoiceUnit(transformedInvoice?.id || '', invoiceUnitId));
  };

  const hasVisibleAddButtons = !isSearchProductsActive && !isCartEmpty && !disabled;

  return (
    <Box
      flex="0 1 auto"
      height="100%"
      display={hasVisibleAddButtons ? 'flex' : 'block'}
      flexDirection={hasVisibleAddButtons && 'column'}
      // need to force render
      key={Number(hasVisibleAddButtons)}
    >
      <Box
        className={classes.body}
        width="100%"
        height="100%"
        flex={hasVisibleAddButtons && '1 1 auto'}
        justifyContent={!isSearchProductsActive && isCartEmpty ? 'center' : 'flex-start'}
        pt={2}
      >
        {!isSearchProductsActive &&
          (!isCartEmpty ? (
            <Box mr={-2} pr={2} height="100%" minHeight="300px">
              {transformedInvoice.invoiceUnits.map(invoiceUnit => {
                const { id, type, subtotalAmount } = invoiceUnit;

                switch (type) {
                  case CartUnitTypes.TOP_UP_BALANCE: {
                    return (
                      <Box key={id} className={classes.cartItem}>
                        <InvoiceTopUpBalanceItem
                          disabled={disabled}
                          onDelete={() => onInvoiceUnitDelete(id)}
                          subtotalAmount={subtotalAmount}
                        />
                      </Box>
                    );
                  }
                  case CartUnitTypes.INVENTORY: {
                    const { inventory, subtotalDiscountAmount } = invoiceUnit;

                    return (
                      <Box key={id} className={classes.cartItem}>
                        <InvoiceProductItem
                          onCountChange={count => onProductCountChange(id, count)}
                          onDelete={() => onInvoiceUnitDelete(id)}
                          product={inventory}
                          disabled={disabled}
                          selectedInvoiceStatus={transformedInvoice?.status}
                          subtotalAmount={subtotalAmount}
                          subtotalDiscountAmount={subtotalDiscountAmount}
                        />
                      </Box>
                    );
                  }
                  case CartUnitTypes.MEMBERSHIP_BUNDLE:
                  case CartUnitTypes.SERVICE_BUNDLE: {
                    return (
                      <InvoiceServiceItem
                        key={id}
                        invoicePackage={transformInvoiceBundleDTO(invoiceUnit)}
                        onDelete={() => onInvoiceUnitDelete(id)}
                        disabled={disabled}
                      />
                    );
                  }
                  case CartUnitTypes.BILLING_SCHEDULE: {
                    const { billingSchedule } = invoiceUnit;

                    return (
                      <Box key={id}>
                        <InvoiceBillingItem
                          billing={billingSchedule}
                          disabled={disabled}
                          onDelete={() => onInvoiceUnitDelete(id)}
                          subtotalAmount={subtotalAmount}
                        />
                      </Box>
                    );
                  }
                  case CartUnitTypes.PAST_DUE: {
                    const { pastDue, pastDueResolve } = invoiceUnit;

                    return (
                      <Box key={id}>
                        <InvoicePastDueItem
                          pastDue={pastDue}
                          disabled={disabled}
                          pastDueResolve={pastDueResolve}
                          onDelete={() => onInvoiceUnitDelete(id)}
                          subtotalAmount={subtotalAmount}
                        />
                      </Box>
                    );
                  }
                  case CartUnitTypes.GIFT_CARD: {
                    const { giftCard } = invoiceUnit;

                    return (
                      <Box key={id}>
                        <InvoiceGiftCardItem
                          disabled={disabled}
                          giftCard={giftCard}
                          onDelete={() => onInvoiceUnitDelete(id)}
                          subtotalAmount={subtotalAmount}
                        />
                      </Box>
                    );
                  }
                  case CartUnitTypes.REACTIVATE_MEMBERSHIP:
                  case CartUnitTypes.REACTIVATE_NON_MEMBERSHIP: {
                    const { reactivatePastDues } = invoiceUnit;

                    return (
                      <InvoiceServiceItem
                        key={id}
                        invoicePackage={transformInvoiceBundleDTO(invoiceUnit)}
                        pastDues={reactivatePastDues}
                      />
                    );
                  }
                  case CartUnitTypes.CANCEL_MEMBERSHIP:
                  case CartUnitTypes.CANCEL_NON_MEMBERSHIP: {
                    const { bundle, cancelPastDues, cancelBillingSchedules } = invoiceUnit;

                    return (
                      <InvoiceCancelPackageItem
                        isMembership={CartUnitTypes.CANCEL_MEMBERSHIP === type}
                        key={id}
                        invoicePackage={bundle}
                        pastDues={cancelPastDues}
                        billingSchedules={cancelBillingSchedules}
                      />
                    );
                  }
                  default:
                    return null;
                }
              })}
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              pt={2}
              pb={2}
              alignSelf="center"
            >
              <Typography color="textSecondary" className={classes.emptyCartText}>
                <FormattedMessage {...messages.emptyCart} />
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
